import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { type Editor } from '@tiptap/react';
import { useRef, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { formatCurrencyValue } from '../../utils';
import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import NumberField from '@/components/NumberField';
import RichTextField from '@/components/RichTextField';
import RichTextFieldReadOnly from '@/components/RichTextFieldReadOnly';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';

interface GetOpstreamCompanyIndustry {
  industryByDeal: {
    cagrPeriod: null | number;
    fragmentation: null | string;
    fragmentationReasoning: null | string;
    growthRate: null | number;
    growthRateReasoning: null | string;
    headwinds: null | string;
    id: string;
    industryCommentary: null | string;
    name: null | string;
    servicableAddressableMarket: null | number;
    tailwinds: null | string;
    totalAddressableMarket: null | number;
  };
}

const GET_OPSTREAM_COMPANY_INDUSTRY = gql`
  query OpstreamCompanyIndustry($deal_id: ID!) {
    industryByDeal(dealOpportunityId: $deal_id) {
      id
      name
      totalAddressableMarket
      servicableAddressableMarket
      growthRate
      growthRateReasoning
      cagrPeriod
      industryCommentary
      fragmentation
      fragmentationReasoning
      headwinds
      tailwinds
    }
  }
`;

const UPDATE_INDUSTRY_METRICS = gql`
  mutation UpdateIndustryMetrics(
    $id: ID!
    $total_addressable_market: Float
    $servicable_addressable_market: Float
    $growth_rate: Float
    $growth_rate_reasoning: String
    $cagr_period: Int
    $industry_commentary: String
    $fragmentation: String
    $headwinds: String
    $tailwinds: String
  ) {
    updateIndustry(
      id: $id
      values: {
        totalAddressableMarket: $total_addressable_market
        servicableAddressableMarket: $servicable_addressable_market
        growthRate: $growth_rate
        growthRateReasoning: $growth_rate_reasoning
        cagrPeriod: $cagr_period
        industryCommentary: $industry_commentary
        fragmentation: $fragmentation
        headwinds: $headwinds
        tailwinds: $tailwinds
      }
    ) {
      ok
    }
  }
`;

const Card = ({ children }: React.PropsWithChildren) => (
  <div className="bg-card @container rounded-md border p-5">{children}</div>
);

const SectionHeader = ({
  actions,
  title
}: {
  actions?: React.ReactNode;
  title: string;
}) => (
  <div className="flex items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    {actions}
  </div>
);

const Wrapper = () => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    throw new Error();
  }

  const { data: data1, loading: loading1 } = useQuery<
    GetOpstreamCompanyIndustry,
    { deal_id: string }
  >(GET_OPSTREAM_COMPANY_INDUSTRY, {
    variables: { deal_id: id }
  });

  if (loading1) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (!data1) {
    return <div>{'Error'}</div>;
  }

  return (
    <InvestmentsDirectIdIndustry
      data1={data1}
      id={id}
    />
  );
};

interface Props {
  data1: GetOpstreamCompanyIndustry;
  id: string;
}

const InvestmentsDirectIdIndustry = ({ data1, id }: Props) => {
  const [updateIndustryMetrics, { loading: isUpdatingIndustryMetrics }] =
    useMutation(UPDATE_INDUSTRY_METRICS);

  const [generalDialogIsOpen, setGeneralDialogIsOpen] = useState(false);
  const [
    headwindsTailwindsAndFragmentationDialogIsOpen,
    setHeadwindsTailwindsAndFragmentationDialogIsOpen
  ] = useState(false);
  const headwindsFieldRef = useRef<Editor>(null);
  const tailwindsFieldRef = useRef<Editor>(null);
  const fragmentationFieldRef = useRef<Editor>(null);
  const industryCommentaryFieldRef = useRef<Editor>(null);
  const handleGeneralFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    const tam = formData.get('tam-field');
    const sam = formData.get('sam-field');
    const growthRate = formData.get('growth-rate-field');
    const growthRateContext = formData.get('growth-rate-context-field');
    const cagrPeriod = formData.get('cagr-period-field');

    try {
      const { error } = await updateIndustryMetrics({
        refetchQueries: [
          {
            query: GET_OPSTREAM_COMPANY_INDUSTRY,
            variables: { deal_id: id }
          }
        ],
        variables: {
          cagr_period:
            cagrPeriod !== '' && cagrPeriod !== null
              ? parseInt(String(cagrPeriod), 10)
              : null,
          growth_rate:
            growthRate != null && growthRate !== '' ? +growthRate / 100 : null,
          growth_rate_reasoning:
            growthRateContext === '' ? null : growthRateContext,
          id: data1.industryByDeal.id,
          servicable_addressable_market: sam ? +sam : null,
          total_addressable_market: tam ? +tam : null
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setGeneralDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleHeadwindsTailwindsAndFragmentationFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (
      !headwindsFieldRef.current ||
      !tailwindsFieldRef.current ||
      !fragmentationFieldRef.current ||
      !industryCommentaryFieldRef.current
    ) {
      toast.error('Unable to read industry commentary fields');
      return;
    }

    try {
      const { error } = await updateIndustryMetrics({
        refetchQueries: [
          {
            query: GET_OPSTREAM_COMPANY_INDUSTRY,
            variables: { deal_id: id }
          }
        ],
        variables: {
          fragmentation: fragmentationFieldRef.current.isEmpty
            ? null
            : fragmentationFieldRef.current.getHTML(),
          headwinds: headwindsFieldRef.current.isEmpty
            ? null
            : headwindsFieldRef.current.getHTML(),
          id: data1.industryByDeal.id,
          industry_commentary: industryCommentaryFieldRef.current.isEmpty
            ? null
            : industryCommentaryFieldRef.current.getHTML(),
          tailwinds: tailwindsFieldRef.current.isEmpty
            ? null
            : tailwindsFieldRef.current.getHTML()
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setHeadwindsTailwindsAndFragmentationDialogIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="min-w-0 space-y-5 px-5 pt-5 pb-16.5 md:pl-14">
          <div className="space-y-2.5">
            <SectionHeader
              actions={
                <Dialog
                  onOpenChange={setGeneralDialogIsOpen}
                  open={generalDialogIsOpen}
                >
                  <DialogTrigger asChild>
                    <EditButton />
                  </DialogTrigger>
                  <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                      <DialogTitle>
                        General
                        <Badge variant="yellow">Unsaved</Badge>
                      </DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                      <form
                        id="general-form"
                        noValidate
                        onSubmit={event => void handleGeneralFormSubmit(event)}
                      >
                        <FieldGroup>
                          <div className="grid grid-cols-2 gap-x-2.5">
                            <Field>
                              <FieldLabel htmlFor="tam-field">TAM</FieldLabel>
                              <InputGroup>
                                <InputGroupAddon>
                                  <InputGroupText>$</InputGroupText>
                                </InputGroupAddon>
                                <NumberField
                                  defaultValue={
                                    data1.industryByDeal
                                      .totalAddressableMarket ?? undefined
                                  }
                                  id="tam-field"
                                  name="tam-field"
                                  placeholder="Enter Value"
                                />
                              </InputGroup>
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="sam-field">SAM</FieldLabel>
                              <InputGroup>
                                <InputGroupAddon>
                                  <InputGroupText>$</InputGroupText>
                                </InputGroupAddon>
                                <NumberField
                                  defaultValue={
                                    data1.industryByDeal
                                      .servicableAddressableMarket ?? undefined
                                  }
                                  id="sam-field"
                                  name="sam-field"
                                  placeholder="Enter Value"
                                />
                              </InputGroup>
                            </Field>
                          </div>
                          <div className="grid grid-cols-2 gap-x-2.5">
                            <Field>
                              <FieldLabel htmlFor="growth-rate-field">
                                Growth Rate
                              </FieldLabel>
                              <InputGroup>
                                <NumberField
                                  defaultValue={(() => {
                                    const v = data1.industryByDeal.growthRate;
                                    if (v == null) return undefined;
                                    return v > 1 ? v : v * 100;
                                  })()}
                                  id="growth-rate-field"
                                  max={100}
                                  min={-100}
                                  name="growth-rate-field"
                                  placeholder="Enter Value"
                                />
                                <InputGroupAddon align="inline-end">
                                  <InputGroupText>%</InputGroupText>
                                </InputGroupAddon>
                              </InputGroup>
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="growth-rate-context-field">
                                Growth Rate Context
                              </FieldLabel>
                              <Textarea
                                defaultValue={
                                  data1.industryByDeal.growthRateReasoning ??
                                  undefined
                                }
                                id="growth-rate-context-field"
                                name="growth-rate-context-field"
                                placeholder="Enter Value"
                              />
                            </Field>
                            <Field>
                              <FieldLabel htmlFor="cagr-period-field">
                                CAGR Period (years)
                              </FieldLabel>
                              <NumberField
                                defaultValue={
                                  data1.industryByDeal.cagrPeriod ?? undefined
                                }
                                id="cagr-period-field"
                                min={1}
                                name="cagr-period-field"
                                placeholder="e.g. 5"
                              />
                            </Field>
                          </div>
                        </FieldGroup>
                      </form>
                    </DialogBody>
                    <DialogFooter>
                      <Button
                        disabled={isUpdatingIndustryMetrics}
                        form="general-form"
                        type="submit"
                      >
                        {isUpdatingIndustryMetrics && <Spinner />}
                        Save
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              }
              title="General"
            />
            <Card>
              <div className="grid gap-5 @md:grid-cols-2">
                <div className="flex min-w-0 items-center justify-between gap-4 text-sm">
                  <span className="shrink-0 text-sm">TAM</span>
                  <TruncatedTextTooltip className="text-muted-foreground min-w-0 text-sm font-medium">
                    {data1.industryByDeal.totalAddressableMarket
                      ? formatCurrencyValue(
                          data1.industryByDeal.totalAddressableMarket
                        )
                      : '-'}
                  </TruncatedTextTooltip>
                </div>
                <div className="flex min-w-0 items-center justify-between gap-4 text-sm">
                  <span className="shrink-0 text-sm">SAM</span>
                  <TruncatedTextTooltip className="text-muted-foreground min-w-0 text-sm font-medium">
                    {data1.industryByDeal.servicableAddressableMarket
                      ? formatCurrencyValue(
                          data1.industryByDeal.servicableAddressableMarket
                        )
                      : '-'}
                  </TruncatedTextTooltip>
                </div>
                <div className="flex min-w-0 items-center justify-between gap-4 text-sm">
                  <span className="shrink-0 text-sm">Growth Rate</span>
                  <TruncatedTextTooltip className="text-muted-foreground min-w-0 text-sm font-medium">
                    {(() => {
                      const v = data1.industryByDeal.growthRate;
                      if (v == null) return '-';
                      const display = v > 1 ? v : v * 100;
                      return `${display.toFixed(1)}%`;
                    })()}
                  </TruncatedTextTooltip>
                </div>
                <div className="flex min-w-0 items-center justify-between gap-4 text-sm">
                  <span className="shrink-0 text-sm">Growth Rate Context</span>
                  <TruncatedTextTooltip className="text-muted-foreground min-w-0 text-sm font-medium">
                    {data1.industryByDeal.growthRateReasoning ?? '-'}
                  </TruncatedTextTooltip>
                </div>
                <div className="flex min-w-0 items-center justify-between gap-4 text-sm">
                  <span className="shrink-0 text-sm">CAGR Period (years)</span>
                  <TruncatedTextTooltip className="text-muted-foreground min-w-0 text-sm font-medium">
                    {data1.industryByDeal.cagrPeriod != null
                      ? `${data1.industryByDeal.cagrPeriod} years`
                      : '-'}
                  </TruncatedTextTooltip>
                </div>
              </div>
            </Card>
          </div>
          <div className="space-y-2.5">
            <SectionHeader
              actions={
                <Dialog
                  onOpenChange={
                    setHeadwindsTailwindsAndFragmentationDialogIsOpen
                  }
                  open={headwindsTailwindsAndFragmentationDialogIsOpen}
                >
                  <DialogTrigger asChild>
                    <EditButton />
                  </DialogTrigger>
                  <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                      <DialogTitle>
                        Headwinds, Tailwinds & Fragmentation
                        <Badge variant="yellow">Unsaved</Badge>
                      </DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                      <form
                        id="headwinds-tailwinds-and-fragmentation-form"
                        noValidate
                        onSubmit={event =>
                          void handleHeadwindsTailwindsAndFragmentationFormSubmit(
                            event
                          )
                        }
                      >
                        <FieldGroup>
                          <Field>
                            <FieldLabel htmlFor="headwinds-field">
                              Headwinds
                            </FieldLabel>
                            <RichTextField
                              defaultValue={
                                data1.industryByDeal.headwinds ?? ''
                              }
                              id="headwinds-field"
                              ref={headwindsFieldRef}
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="tailwinds-field">
                              Tailwinds
                            </FieldLabel>
                            <RichTextField
                              defaultValue={
                                data1.industryByDeal.tailwinds ?? ''
                              }
                              id="tailwinds-field"
                              ref={tailwindsFieldRef}
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="fragmentation-field">
                              Fragmentation
                            </FieldLabel>
                            <RichTextField
                              defaultValue={
                                data1.industryByDeal.fragmentation ?? ''
                              }
                              id="fragmentation-field"
                              ref={fragmentationFieldRef}
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="industry-commentary-field">
                              Industry Commentary
                            </FieldLabel>
                            <RichTextField
                              defaultValue={
                                data1.industryByDeal.industryCommentary ?? ''
                              }
                              id="industry-commentary-field"
                              ref={industryCommentaryFieldRef}
                            />
                          </Field>
                        </FieldGroup>
                      </form>
                    </DialogBody>
                    <DialogFooter>
                      <Button
                        disabled={isUpdatingIndustryMetrics}
                        form="headwinds-tailwinds-and-fragmentation-form"
                        type="submit"
                      >
                        {isUpdatingIndustryMetrics && <Spinner />}
                        Save
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              }
              title="Headwinds, Tailwinds & Fragmentation"
            />
            <Card>
              <div className="space-y-5">
                <div className="space-y-2">
                  <div className="text-sm font-medium">Headwinds</div>
                  <RichTextFieldReadOnly
                    html={data1.industryByDeal.headwinds ?? ''}
                  />
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-medium">Tailwinds </div>
                  <RichTextFieldReadOnly
                    html={data1.industryByDeal.tailwinds ?? ''}
                  />
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-medium">Fragmentation</div>
                  <RichTextFieldReadOnly
                    html={data1.industryByDeal.fragmentation ?? ''}
                  />
                </div>
                <div className="space-y-2">
                  <div className="text-sm font-medium">Industry Commentary</div>
                  <RichTextFieldReadOnly
                    html={data1.industryByDeal.industryCommentary ?? ''}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default Wrapper;
